import FrameBuffer from "_src/engine/framebuffer";
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import {
  blendFactor,
  primitiveType,
  pixelBufferType,
  attachmentId
} from "_src/engine/constants";
import Camera from "_src/engine/camera";

import {postEffects} from "_src/constants";
import Pass from "_src/render_graph/pass";
import resourceManager from "_src/render_graph/resource_manager";

import VignetteShader from "_src/shaders/vignette/vignette";
import ColorBalanceShader from "_src/shaders/color_balance/color_balance";
import CopyShader from "_src/shaders/copy/copy";
import GrainShader from "_src/shaders/grain/grain";
import SharpnessShader from "_src/shaders/sharpness/sharpness";

export default class PostEffectPass extends Pass {

  constructor(attachmentsStorage) {
    super(attachmentsStorage);
    this.enabledEffects = [];
    this.init();
  }

  init() {

    for (let pass in postEffects) {
      const passID = postEffects[pass];
      this.enabledEffects[passID] = false;
    }

    const camera = new Camera();

    const colorBalanceScene = this._addScene(camera);
    const sharpnessScene = this._addScene(camera);
    const grainScene = this._addScene(camera);
    const vignetteScene = this._addScene(camera);
    const copyScene = this._addScene(camera);

    grainScene.clearedColor = false;
    vignetteScene.clearedColor = false;
    copyScene.clearedColor = false;

    const colorBalanceShader = new ColorBalanceShader({transparent : false});
    const sharpnessShader = new SharpnessShader({transparent : false});
    const grainShader = new GrainShader({transparent : true});
    const vignetteShader = new VignetteShader({transparent : true});
    const copyShader = new CopyShader();

    grainShader.setSourceBlendFactor(blendFactor.ONE);
    grainShader.setDestBlendFactor(blendFactor.ONE);
    vignetteShader.setSourceBlendFactor(blendFactor.ONE);

    const colorBalanceScreenPlane = objFactory.createDrawable(colorBalanceShader);
    colorBalanceScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    colorBalanceScene.add(colorBalanceScreenPlane);

    const sharpnessScreenPlane = objFactory.createDrawable(sharpnessShader);
    sharpnessScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    sharpnessScene.add(sharpnessScreenPlane);

    const grainScreenPlane = objFactory.createDrawable(grainShader);
    grainScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    grainScene.add(grainScreenPlane);

    const vignetteScreenPlane = objFactory.createDrawable(vignetteShader);
    vignetteScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    vignetteScene.add(vignetteScreenPlane);

    const copyScreenPlane = objFactory.createDrawable(copyShader);
    copyScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    copyScene.add(copyScreenPlane);

  }

  setEnabledEffect(effect, enabled) {
    this.enabledEffects[effect] = enabled;
    this.update();
  }

  update() {

    const scenes = this.getScenes();

    let inputTexture = this.attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE];
    let needsResolve = false;

    for (let passID = 0; passID < this.enabledEffects.length; ++passID) {

      const scene = scenes[passID];
      const enabled = this.enabledEffects[passID];

      scene.setEnabled(enabled);

      if (!enabled) {
        continue;
      }

      const width = scene.camera.viewport[2];
      const height = scene.camera.viewport[3];

      let framebuffer = null;

      switch(passID) {
        case postEffects.COLOR_BALANCE:
        case postEffects.SHARPNESS:
          framebuffer = this._initChainFramebuffer(inputTexture, passID, width, height);
          inputTexture = framebuffer.colorAttachments[0];
          needsResolve = true;
          break;
        case postEffects.GRAIN:
        case postEffects.VIGNETTE:
          framebuffer = this._initDirectFramebuffer(inputTexture);
          break;
      }

      scene.setFramebuffer(framebuffer);

    }

    const copyScene = scenes[scenes.length - 1];
    copyScene.setEnabled(needsResolve);

    if (needsResolve) {
      const framebuffer = this._initCopyFramebuffer(copyScene, inputTexture);
      copyScene.setFramebuffer(framebuffer);
    }

  }

  _initChainFramebuffer(inputTexture, sceneID, width, height) {
    const colorTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });

    const scene = this.getScenes()[sceneID];
    const screenPlane = scene.getChildren()[0];
    screenPlane.setTexture(inputTexture, 0);

    return new FrameBuffer([colorTexture], null);
  }

  _initDirectFramebuffer(inputTexture) {
    return new FrameBuffer([inputTexture], null);
  }

  _initCopyFramebuffer(copyScene, inputTexture) {
    const colorTexture = this.attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE];

    const screenPlane = copyScene.getChildren()[0];
    screenPlane.setTexture(inputTexture, 0);

    return new FrameBuffer([colorTexture], null);
  }

}
